<template>
  <div class="user-registration-container">
    <el-card shadow="hover" class="registration-card">
      <div class="header-section">
        <h2 class="page-title">إدارة حسابات المستخدمين</h2>
        <el-tabs v-model="activeTab" type="card">
          <el-tab-pane label="إنشاء / تعديل حساب" name="register"></el-tab-pane>
          <el-tab-pane label="المستخدمون الحاليون" name="existing"></el-tab-pane>
        </el-tabs>
      </div>

      <div v-if="activeTab === 'register'">
        <el-form ref="formRef" :model="form" label-width="180px" :rules="rules">
          <el-form-item label="نوع المستخدم" prop="user_type" :error="formErrors.user_type ? formErrors.user_type[0] : ''">
            <el-select v-model="form.user_type" placeholder="اختر نوع المستخدم">
              <el-option v-for="type in userTypes" :key="type.value" :label="type.label" :value="type.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="الاسم الكامل" prop="name" :error="formErrors.name ? formErrors.name[0] : ''">
            <el-input v-model="form.name" />
          </el-form-item>
          <el-form-item label="رقم الهاتف" prop="phone" :error="formErrors.phone ? formErrors.phone[0] : ''">
            <el-input v-model="form.phone" />
          </el-form-item>
          <el-form-item label="البريد الإلكتروني" prop="email" :error="formErrors.email ? formErrors.email[0] : ''">
            <el-input v-model="form.email" type="email" />
          </el-form-item>
          <el-form-item label="اسم المستخدم" prop="username" :error="formErrors.username ? formErrors.username[0] : ''">
            <el-input v-model="form.username" />
          </el-form-item>
          <el-form-item :label="form.id ? 'كلمة مرور جديدة (اختياري)' : 'كلمة المرور'" prop="password" :error="formErrors.password ? formErrors.password[0] : ''">
            <el-input v-model="form.password" show-password placeholder="اتركه فارغاً لعدم التغيير عند التعديل" />
          </el-form-item>
          <el-form-item label="العنوان" prop="address" :error="formErrors.address ? formErrors.address[0] : ''">
            <el-input v-model="form.address" type="textarea" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="submitForm" :loading="loading">
              {{ form.id ? 'تحديث حساب المستخدم' : 'إنشاء حساب المستخدم' }}
            </el-button>
            <el-button @click="resetForm">إعادة تعيين</el-button>
          </el-form-item>
        </el-form>
      </div>

      <div v-else>
        <div class="filter-section">
          <el-select v-model="listFilters.user_type" placeholder="فلترة حسب نوع المستخدم" clearable @change="fetchExistingUsers">
            <el-option v-for="type in userTypes" :key="type.value" :label="type.label" :value="type.value" />
          </el-select>
          <el-input v-model="listFilters.search" placeholder="بحث بالاسم، الهاتف، إلخ." clearable @clear="fetchExistingUsers" @keyup.enter="fetchExistingUsers">
            <template #append>
              <el-button @click="fetchExistingUsers"><el-icon><Search /></el-icon></el-button>
            </template>
          </el-input>
        </div>

        <el-table :data="existingUsers" border style="width: 100%" v-loading="listLoading">
          <el-table-column prop="name" label="الاسم" width="180" />
          <el-table-column prop="user_type" label="نوع المستخدم" width="150" />
          <el-table-column prop="username" label="اسم المستخدم" width="150" />
          <el-table-column prop="phone" label="الهاتف" width="150" />
          <el-table-column prop="email" label="البريد الإلكتروني" min-width="220" />
          <el-table-column label="الإجراءات" width="220" fixed="right">
            <template #default="{row}">
              <el-button size="small" @click="editUser(row)">تعديل الحساب</el-button>
              <el-button
                size="small"
                type="success"
                @click="navigateToProfile(row)"
                v-if="row.user_type === 'student' || row.user_type === 'teacher'"
              >
                عرض الملف الشخصي
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          v-model:currentPage="pagination.current_page"
          :page-sizes="[15, 30, 50, 100]"
          v-model:page-size="pagination.per_page"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          background
          :hide-on-single-page="false"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage } from 'element-plus';
import { Search } from '@element-plus/icons-vue';

export default {
  name: 'UserManagement',
  components: { Search },
  data() {
    const validatePassword = (rule, value, callback) => {
      // الحالة 1: إنشاء مستخدم جديد (this.form.id يكون null)
      if (!this.form.id) {
        if (!value) {
          return callback(new Error('Password is required for new users'));
        }
        if (value.length < 8) {
          return callback(new Error('Password must be at least 8 characters'));
        }
      }
      // الحالة 2: تحديث مستخدم، وكلمة المرور ليست فارغة
      else if (value && value.length > 0) {
        if (value.length < 8) {
          return callback(new Error('Password must be at least 8 characters'));
        }
      }
      // الحالة 3: تحديث مستخدم، وكلمة المرور فارغة (وهذا مسموح)
      return callback(); // لا يوجد خطأ
    };
    return {
      activeTab: 'register',
      form: this.getInitialForm(),
      rules: {
        user_type: [{ required: true, message: 'Please select user type', trigger: 'change' }],
        name: [{ required: true, message: 'Please input full name', trigger: 'blur' }],
        phone: [{ required: true, message: 'Please input phone number', trigger: 'blur' }],
        username: [{ required: true, message: 'Please input username', trigger: 'blur' }],
        address: [{ required: true, message: 'Please input address', trigger: 'blur' }],
        password: [{ validator: validatePassword, trigger: 'blur' }]
      },
      formErrors: {},
      // القائمة الكاملة لأنواع المستخدمين
      userTypes: [
          { value: 'admin', label: 'Admin' }, { value: 'teacher', label: 'Teacher' },
          { value: 'staff', label: 'Staff' }, { value: 'student', label: 'Student' },
          { value: 'parent', label: 'Parent' }, { value: 'driver', label: 'Driver' },
          { value: 'financial', label: 'Financial' }, { value: 'doctor', label: 'Doctor' },
          { value: 'moder', label: 'Moderator' }, { value: 'hr', label: 'HR' },
          { value: 'prin', label: 'Principal' }, { value: 'student_m', label: 'Student Manager' },
          { value: 'cashier', label: 'Cashier' }, { value: 'student supervisor', label: 'Student Supervisor' },
          { value: 'developer', label: 'Developer' },
      ],
      loading: false,
      existingUsers: [],
      listLoading: false,
      listFilters: { user_type: '', search: '' },
      pagination: { current_page: 1, per_page: 15, total: 0 },
      api: null,
    };
  },
  created() {
    this.api = axios.create({
      baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
    });

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    });
  
    this.fetchExistingUsers();
  },
  methods: {
    getInitialForm() {
      return { id: null, user_type: '', name: '', phone: '', email: '', username: '', password: '', address: '' };
    },
    async submitForm() {
      if (!this.$refs.formRef) return;
      await this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;

        this.loading = true;
        this.formErrors = {}; 
        const formData = { ...this.form };

        try {
          let response;
          if (formData.id) {
            if (!formData.password) delete formData.password;
            response = await this.api.put(`/users-controller/${formData.id}`, formData);
            ElMessage.success('User account updated successfully!');
          } else {
            response = await this.api.post('/users-controller', formData);
            ElMessage.success(response.data.message || 'User account created successfully!');
          }
          this.resetForm();
          this.activeTab = 'existing';
          this.fetchExistingUsers();
        } catch (error) {
          console.error('Error saving user account:', error);
          if (error.response && error.response.status === 422) {
            this.formErrors = error.response.data.errors;
            ElMessage.error('Please check the form for errors.');
          } else {
            const errorMsg = error.response?.data?.message || 'An unexpected error occurred.';
            ElMessage.error(errorMsg);
          }
        } finally {
          this.loading = false;
        }
      });
    },
    resetForm() {
      this.form = this.getInitialForm();
      this.formErrors = {};
      this.$nextTick(() => { if (this.$refs.formRef) this.$refs.formRef.clearValidate(); });
    },
    editUser(user) {
      this.activeTab = 'register';
      this.form = {
        id: user.id, user_type: user.user_type, name: user.name,
        phone: user.phone, email: user.email || '', username: user.username || '',
        password: '', address: user.address || '',
      };
    },

    navigateToProfile(user) {
        // --- إضافة تحقق إضافي لتجنب الأخطاء ---
        console.log("Navigating for user:", user); // للمساعدة في تتبع البيانات
        
        if (user.user_type === 'student' && user.student_profile && user.student_profile.id) {
            this.$router.push({ name: 'EditStudent', params: { id: user.student_profile.id } });
        } else if (user.user_type === 'teacher' && user.employee_profile && user.employee_profile.id) {
            this.$router.push({ name: 'EditEmployee', params: { id: user.employee_profile.id } });
        } else {
            ElMessage.info(`This user (${user.name}) does not have an associated profile record yet, or the profile data is incomplete.`);
        }
    },

    async fetchExistingUsers() {
      this.listLoading = true;
      try {
        const params = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          user_type: this.listFilters.user_type || undefined,
          search: this.listFilters.search || undefined,
        };
        const response = await this.api.get('/users-controller', { params });
        
        // هذا الجزء هو المسؤول عن تحديث بيانات الجدول والـ pagination
        this.existingUsers = response.data.data;
        this.pagination.current_page = response.data.current_page;
        this.pagination.per_page = response.data.per_page;
        this.pagination.total = response.data.total;
      } catch (error) {
        console.error('Error fetching existing users:', error);
        ElMessage.error('Failed to fetch users.');
      } finally {
        this.listLoading = false;
      }
    },

    handleSizeChange(val) {
      this.pagination.per_page = val;
      this.pagination.current_page = 1;
      this.fetchExistingUsers();
    },

    handleCurrentChange(val) {
      this.pagination.current_page = val;
      this.fetchExistingUsers();
    },
  }
};
</script>

<style scoped>
/* ... نفس الـ styles لديك ... */
.user-registration-container { max-width: 1200px; margin: 0 auto; padding: 20px; }
.registration-card { border-radius: 8px; }
.header-section { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.page-title { margin: 0; font-size: 24px; color: #303133; }
.filter-section { display: flex; gap: 10px; margin-bottom: 20px; }
.el-pagination { margin-top: 20px; text-align: right; }
@media (max-width: 768px) {
    .header-section { flex-direction: column; align-items: flex-start; gap: 10px; }
    .filter-section { flex-direction: column; }
}
</style>